import { Tooltip } from '../../components/Tooltip/Tooltip';
import { type CSSProperties, type ReactNode, useRef } from 'react';
import { isTruncated } from '../is-truncated';

const DEFAULT_TOOLTIP_STYLE: CSSProperties = {
    whiteSpace: 'normal',
};

interface OverflowTooltipLabelProps {
    label: string;
    className?: string;
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right' | 'mouse';
    tooltipStyle?: CSSProperties;
    tooltipMy?: string;
    tooltipAt?: string;
}

function getDefaultTooltipAlignment(
    tooltipPosition: OverflowTooltipLabelProps['tooltipPosition'],
): Pick<OverflowTooltipLabelProps, 'tooltipMy' | 'tooltipAt'> {
    if (tooltipPosition === 'bottom') {
        return {
            tooltipMy: 'center top',
            tooltipAt: 'center bottom',
        };
    }

    if (tooltipPosition === 'left') {
        return {
            tooltipMy: 'right center',
            tooltipAt: 'left center',
        };
    }

    if (tooltipPosition === 'right') {
        return {
            tooltipMy: 'left center',
            tooltipAt: 'right center',
        };
    }

    return {
        tooltipMy: 'center bottom',
        tooltipAt: 'center top',
    };
}

export function OverflowTooltipLabel({
    label,
    className,
    tooltipPosition = 'top',
    tooltipStyle = DEFAULT_TOOLTIP_STYLE,
    tooltipMy,
    tooltipAt,
}: OverflowTooltipLabelProps): ReactNode {
    const labelRef = useRef<HTMLSpanElement>(null);
    const tooltipRef = useRef<Tooltip>(null);
    const defaultAlignment = getDefaultTooltipAlignment(tooltipPosition);

    return (
        <>
            <Tooltip
                ref={tooltipRef}
                target={labelRef}
                className="ics-overflow-tooltip"
                content={label}
                position={tooltipPosition}
                style={tooltipStyle}
                my={tooltipMy ?? defaultAlignment.tooltipMy}
                at={tooltipAt ?? defaultAlignment.tooltipAt}
                onBeforeShow={(event) => isTruncated(event.target)}
                onShow={(event) => {
                    if (tooltipPosition !== 'bottom') {
                        return;
                    }

                    window.setTimeout(() => {
                        const tooltip = tooltipRef.current?.getElement();

                        if (!tooltip) {
                            return;
                        }

                        const targetBounds =
                            event.target.getBoundingClientRect();
                        const tooltipBounds = tooltip.getBoundingClientRect();
                        const fitsBelow =
                            tooltipBounds.bottom <= window.innerHeight;

                        if (fitsBelow) {
                            return;
                        }

                        const left = Math.min(
                            Math.max(
                                targetBounds.left +
                                    targetBounds.width / 2 -
                                    tooltipBounds.width / 2,
                                0,
                            ),
                            window.innerWidth - tooltipBounds.width,
                        );
                        const top = Math.max(
                            targetBounds.top +
                                window.scrollY -
                                tooltipBounds.height,
                            window.scrollY,
                        );

                        tooltip.style.left = `${left + window.scrollX}px`;
                        tooltip.style.top = `${top}px`;
                        tooltip.classList.replace(
                            'p-tooltip-bottom',
                            'p-tooltip-top',
                        );
                    }, 0);
                }}
            />
            <span ref={labelRef} className={className}>
                {label}
            </span>
        </>
    );
}

export function renderOverflowTooltipLabel(
    label: string | null | undefined,
    className: string,
): ReactNode | null {
    if (!label) {
        return null;
    }

    return <OverflowTooltipLabel label={label} className={className} />;
}
